/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <quantity-selector-component> used by the PDP buy
buttons and cart line items. It handles increasing, decreasing, and setting quantities, enforces
min/max quantity rules, and instantly updates the Shopify cart via AJAX if data-instant-add is
set. Cart line items (data-cart-line set) additionally dispatch QuantitySelectorUpdateEvent, read
by component-cart-items.js to update the line's price/subtotal.
It is registered as a custom element if not already defined.

*/

import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

   /**
   * Syncs the quantity input value with the current cart quantity for this variant.
   * Updates the input value to match the cart, or sets to 0 if not present.
   * Optionally, you can call this after a cart update or on component init.
   */
  syncQuantityWithCart() {
    const input = this.refs.quantityInput;
    const variantId = input.dataset.variantId;
    if (!variantId) return;
    
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const item = cart.items.find(i => String(i.variant_id) === String(variantId));
        input.value = item ? item.quantity : 0;
      })
      .catch(error => {
        console.error('Failed to sync quantity with cart:', error);
      });
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event. Dispatches QuantitySelectorUpdateEvent only for a cart
   * line item (data-cart-line set, read by component-cart-items.js) — the PDP buy-buttons
   * quantity-selector has nothing to tell on change; the new quantity is only read when the
   * product form itself is submitted.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;
    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    if (typeof quantityInput.dataset.cartLine !== 'undefined') {
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
    }

    // Instant add to cart
    if (this.hasAttribute('data-instant-add')) {
      this.#instantAddToCart(newValue);
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (parseInt(newValue) < parseInt(min)) quantityInput.value = min;
    if (parseInt(newValue) > parseInt(max)) quantityInput.value = max;
  };

  /**
   * Updates the Shopify cart instantly if data-instant-add is set.
   * @param {number} quantity
   */

 
  #instantAddToCart(quantity) {
    const input = this.refs.quantityInput;
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    this.#disableCartItems();

    const body = JSON.stringify({
      updates: {
        [variantId]: Number(quantity)
      }
    });

    fetch(Theme.routes.cart_update_url,  fetchConfig('json', { body }))
      .then(res => res.json())
      .then(cart => {
        this.dispatchEvent(
          new CartUpdateEvent(
            {},
            this.dataset.sourceId || null,
            {
              source: 'quantity-selector',
              variantId: variantId,
              itemCount: cart.item_count,
            }
          )
        );
      })
      .catch(error => {
        this.dispatchEvent(
          new CartErrorEvent(
            this.dataset.sourceId || null,
            error.message || 'Cart update failed'
          )
        );
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
